import { CanvasTypes } from "container/Report/Components/TypeBtn/TypeBtn";
import { ReportStepType } from "hooks/Report/step/useUpdateStepList";
import { ColumnInfoType } from "store/project/data/useTableDataStore";
import { LibraryDataType, VersionDataType } from "store/useLibraryDataStore";
import { ProjectDataType } from "store/useProjectDataStore";
import {
  CanvasModeType,
  ReportColumnType,
  ReportDatasetType,
  ReportStepItemType,
  ShapeType,
} from "types/report/ReportTypes";
import { create } from "zustand";

type ReportModeType = "edit" | "view";

export interface ReportDataType {
  createdDate: string;
  lastModifiedDate: string;
  createdBy: string;
  lastModifiedBy: string;
  libraryDetailId: string;
  reportId: string;
  reportName: string;
  description: string;
  tagsList: string[];
  madeBy: string;
}

interface ShapeModeType {
  isShapeMode: boolean;
  shapeType: "square" | "ellipse" | "polygon" | "line" | "image" | "text";
}

export interface ReportColumnInfoType extends ColumnInfoType {
  id: number;
}

export interface NewReportDataType {
  reportName: string;
  description: string;
  tagsList: string[];
  folderId?: string;
  reportId?: string;
}

export interface NewFolderDataType {
  folderName: string;
  description: string;
  tagsList: string[];
  subFiles: string[];
  folderId?: string;
}

interface ReportDataStore {
  reportData: ReportDataType;
  selectedDatasetMode: boolean;
  selectedDataset: ReportDatasetType | null;
  canvasRatio: { value: CanvasTypes; width: number; height: number };
  zoomLevel: number;
  canvasMode: CanvasModeType; // 현재 레포트 내 캔버스 관련 모드가 무엇인지 (기본, 이동, 생성)
  shapeCreateMode: ShapeType;
  shapeMode: ShapeModeType;
  reportStepList: ReportStepItemType[];
  lastStepData: ReportStepItemType | null; // 21번째 스텝 (맨 아래 스텝 삭제 시 그 이전 스텝으로 이동하기 위한 데이터)
  stepUpdateType: ReportStepType | "";
  selectedStep: ReportStepItemType | null;
  reportDatasets: LibraryDataType[] | VersionDataType[];
  reportProjects: ProjectDataType[];
  reportColumns: ReportColumnType[];
  isReportInfoDashboard: boolean;
  newReportData: NewReportDataType; // 새로운 레포트를 생성할 때, 탭을 이동하더라도 데이터를 유지시키기 위한 데이터
  newFolderData: NewFolderDataType; // 새로운 폴더를 생성할 때, 탭을 이동하더라도 데이터를 유지시키기 위한 데이터
  reportMode: ReportModeType;
  setReportData: (newData: Partial<ReportDataType>) => void;
  setSelectedDatasetMode: (value: boolean) => void;
  setSelectedDataset: (dataset: ReportDatasetType) => void;
  setCanvasRatio: (ratio: CanvasTypes) => void;
  setZoomLevel: (zoomLevel: number) => void;
  setCanvasMode: (canvasMode: CanvasModeType) => void;
  setShapeMode: (shapeMode: ShapeModeType) => void;
  setShapeCreateMode: (type: ShapeType) => void;
  setReportDatasets: (datasets: LibraryDataType[] | VersionDataType[]) => void;
  setReportProjects: (projects: ProjectDataType[]) => void;
  setReportColumns: (columns: ReportColumnType[]) => void;
  setReportStepList: (steps: ReportStepItemType[]) => void;
  setLastStepData: (steps: ReportStepItemType) => void;
  setStepUpdateType: (type: ReportStepType | "") => void;
  setSelectedStep: (step: ReportStepItemType) => void;
  setIsReportInfoDashboard: (value: boolean) => void;
  setNewReportData: (data: NewReportDataType) => void;
  setNewFolderData: (data: NewFolderDataType) => void;
  setReportMode: (mode: ReportModeType) => void;
  resetReportData: () => void;
}

const useReportDataStore = create<ReportDataStore>((set) => ({
  reportData: {
    createdDate: "",
    lastModifiedDate: "",
    createdBy: "",
    lastModifiedBy: "",
    libraryDetailId: "",
    reportId: "",
    reportName: "",
    description: "",
    tagsList: [],
    madeBy: "",
  },
  reportDatasets: [],
  reportProjects: [],
  reportColumns: [],
  selectedDatasetMode: false,
  selectedDataset: null,
  canvasRatio: { value: "16:9", width: 1920, height: 1080 },
  zoomLevel: 50,
  canvasMode: "default",
  shapeCreateMode: "square",
  shapeMode: { isShapeMode: false, shapeType: "square" },
  reportStepList: [],
  lastStepData: null,
  stepUpdateType: "",
  selectedStep: null,
  isReportInfoDashboard: false,
  newReportData: {
    reportName: "",
    description: "",
    tagsList: [],
    reportId: "",
    folderId: "",
  },
  newFolderData: {
    folderName: "",
    description: "",
    tagsList: [],
    subFiles: [],
    folderId: "",
  },
  reportMode: "view",
  setReportData: (newData: Partial<ReportDataType>) => {
    set((state) => ({
      reportData: { ...state.reportData, ...newData },
    }));
  },
  setReportDatasets: (datasets: LibraryDataType[] | VersionDataType[]) => {
    set({ reportDatasets: datasets });
  },
  setReportProjects: (projects: ProjectDataType[]) => {
    set({ reportProjects: projects });
  },
  setReportColumns: (columns: ReportColumnType[]) => {
    set({ reportColumns: columns });
  },
  setSelectedDatasetMode: (value: boolean) => {
    set({ selectedDatasetMode: value });
  },
  setSelectedDataset: (dataset: ReportDatasetType) => {
    set({ selectedDataset: dataset });
  },
  setCanvasRatio: (ratio: CanvasTypes) => {
    switch (ratio) {
      case "4:3":
        set({ canvasRatio: { value: ratio, width: 1024, height: 768 } });
        break;
      case "16:9":
        set({ canvasRatio: { value: ratio, width: 1920, height: 1080 } });
        break;
      case "21:9":
        set({ canvasRatio: { value: ratio, width: 2560, height: 1080 } });
        break;
      case "vertical-A4":
        set({ canvasRatio: { value: ratio, width: 595, height: 842 } });
        break;
      case "horizontal-A4":
        set({ canvasRatio: { value: ratio, width: 842, height: 595 } });
        break;
    }
  },
  setZoomLevel: (zoomLevel: number) => {
    set({ zoomLevel: zoomLevel });
  },
  setCanvasMode: (canvasMode: CanvasModeType) =>
    set({ canvasMode: canvasMode }),
  setShapeCreateMode: (type: ShapeType) => set({ shapeCreateMode: type }),
  setShapeMode: (shapeMode: ShapeModeType) => set({ shapeMode: shapeMode }),
  setReportStepList: (steps: ReportStepItemType[]) =>
    set({ reportStepList: steps }),
  setLastStepData: (steps: ReportStepItemType) => set({ lastStepData: steps }),
  setStepUpdateType: (type: ReportStepType | "") =>
    set({ stepUpdateType: type }),
  setSelectedStep: (step: ReportStepItemType) => set({ selectedStep: step }),
  setIsReportInfoDashboard: (value: boolean) =>
    set({ isReportInfoDashboard: value }),
  setNewReportData: (data: NewReportDataType) => set({ newReportData: data }),
  setNewFolderData: (data: NewFolderDataType) => set({ newFolderData: data }),
  setReportMode: (mode: ReportModeType) => set({ reportMode: mode }),
  resetReportData: () => {
    set({
      reportData: {
        createdDate: "",
        lastModifiedDate: "",
        createdBy: "",
        lastModifiedBy: "",
        libraryDetailId: "",
        reportId: "",
        reportName: "",
        description: "",
        tagsList: [],
        madeBy: "",
      },
      reportDatasets: [],
      reportProjects: [],
      reportColumns: [],
      selectedDatasetMode: false,
      selectedDataset: null,
      zoomLevel: 50,
      canvasMode: "default",
      shapeCreateMode: "square",
      shapeMode: { isShapeMode: false, shapeType: "square" },
      reportStepList: [],
      lastStepData: null,
      stepUpdateType: "",
      selectedStep: null,
      isReportInfoDashboard: false,
      reportMode: "view",
    });
  },
}));

// 값의 변경을 감지하여 콘솔에 출력하는 코드
useReportDataStore.subscribe((state) => {
  // console.log("reportData => ", state.reportData);
});

export default useReportDataStore;
